import { memo, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { useNavigate } from 'react-router-dom';
// Local files
import FileUploader from 'components/Blobs/FileUploader/FileUploader';
import DocList from 'components/EsignTemplates/List/List';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useApp from 'hooks/useApp';
import useBlob from 'hooks/useBlob';
import useChecksum from 'hooks/useChecksum';
import useEsignTemplates from 'hooks/useEsignTemplates';
import { appSelectors } from 'features/app'
import { checkEmptyString } from 'helpers';

const EsignFlowDrawer = () => {
  const navigate = useNavigate();
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const { setEsignFlowDrawer } = useApp();
  const { processBlob } = useBlob();
  const { processFiles } = useChecksum();
  const { createEsignTemplate } = useEsignTemplates();
  const { open } = useCustomSelector(appSelectors.getEsignFlowDrawerData);
  const [googleFormId, setGoogleFormId] = useState({ value: '', error: '' });
  const [file, setFile] = useState(null);

  const handleUpload = (files) => processFiles(files, ({ file, checksum }) => processBlob({ file, checksum }, blob => setFile(blob)));
  const handleClose = () => setEsignFlowDrawer({ open: false });
  const handleExited = () => { 
    setGoogleFormId({ value: '', error: '' });
    setFile(null);
  };
  const handleSave = () => {
    let hasError = false;
    if (!checkEmptyString(googleFormId.value)) {
      setGoogleFormId(prev => ({ ...prev, error: `Google Form Id can't be empty` }) );
      hasError = true;
    }
    if (hasError) return;

    const esignTemplate = { template_attributes :{ file: file.id, name: file.filename }, google_form_id: googleFormId.value };

    createEsignTemplate(esignTemplate)
    .then(({ payload, error }) => {
      if (!error) {
        const { esign_template: { id } } = payload;
        navigate(`/esign_templates/${id}`);
      }
    })
  };

  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      onClose={handleClose}
      SlideProps={{ onExited: handleExited }}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={handleClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
          Create Template
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
        {!!!file ? (
          <FileUploader
            onFilesChange={({ target: { files } }) => handleUpload(files)}
            onDrop={handleUpload}
            sx={{ width: '100%' }}
          />
          ) : (
            <Box>
              <DocList
                documents={[file]}
                onDelete={() => setFile(null)}
              />
            </Box>
        )}
        <TextField
          fullWidth
          margin='normal'
          label='Google form id'
          value={googleFormId.value}
          onChange={({ target: { value } }) => setGoogleFormId({ value, error: '' })}
          helperText={googleFormId.error}
          error={!!googleFormId.error}
        />
        <Button
          variant='contained'
          sx={{ display: 'flex', mx: 'auto', px: 5, mt: 3 }}
          onClick={handleSave}
        >
          Save
        </Button>
      </Box>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <EsignFlowDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);